import { instantFrom, wallClockIn } from './timestamp';

/**
 * Display formatting for a single instant, read in an explicit timezone.
 *
 * Formatting belongs in the date layer — the anchor and the rendering are one decision, and
 * splitting them across modules is how they drift apart. Ranges come from dashing-ui's
 * `formatDateRange` instead, so the app and the date components agree by construction.
 */

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

type RangeBound = Date | string | null | undefined;

const toWallClock = (value: RangeBound, timezone: string): Temporal.PlainDateTime | null => {
  const instant = instantFrom(value);
  return instant ? wallClockIn(instant, timezone) : null;
};

const TOKENS = /MMMM|MMM|YYYY|mm|D|h|a|A/g;

/** Full month names, indexed as `MONTHS`. */
const MONTHS_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

/**
 * Format a single instant for display, read in an explicit timezone.
 *
 * `pattern` takes a fixed token set — `MMMM`, `MMM`, `YYYY`, `D`, `h`, `mm`, `a`, `A` — matching
 * the dayjs patterns the call sites already used, so each conversion is one line whose output is
 * unchanged in the browser zone. Anything else is copied through, so a token sitting inside
 * literal text would be substituted. Returns `''` for a value naming no instant.
 */
export function formatInstant(value: RangeBound, pattern: string, timezone: string): string {
  const instant = toWallClock(value, timezone);
  if (instant) return render(instant, pattern);
  // A zone-less string names no instant but does name a wall clock, and `PlainDateTime.from`
  // drops an offset rather than reading one — so it only ever sees what the instant path refused.
  try {
    return render(Temporal.PlainDateTime.from(value as string), pattern);
  } catch {
    return '';
  }
}

function render(wallClock: Temporal.PlainDateTime, pattern: string): string {
  const replacements: Record<string, string> = {
    MMMM: MONTHS_LONG[wallClock.month - 1],
    MMM: MONTHS[wallClock.month - 1],
    YYYY: String(wallClock.year),
    D: String(wallClock.day),
    h: String(wallClock.hour % 12 || 12),
    mm: String(wallClock.minute).padStart(2, '0'),
    a: wallClock.hour < 12 ? 'am' : 'pm',
    A: wallClock.hour < 12 ? 'AM' : 'PM',
  };

  return pattern.replace(TOKENS, (token) => replacements[token]);
}
